import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from "@angular/common";
import { environment } from 'src/environments/environment';

/**
 * Provides an abstraction to interact with cookies
 */
@Injectable({
  providedIn: 'root'
})
export class CookieService {

  constructor(@Inject(DOCUMENT) private document: Document) { }

  public setCookie(cookie: Cookie) {
    let cookieStr = `${cookie.name}=${cookie.value}`;

    if (cookie.domain) {
      cookieStr += `; Domain=${cookie.domain}`;
    }

    // Don't use simple boolean check because we allow the path to be false and empty string
    if (cookie.path != null) {
      cookieStr += `; Path=${cookie.path}`;
    }

    if (cookie.maxAge) {
      cookieStr += `; Max-Age=${cookie.maxAge}`;
    }

    if (cookie.expires) {
      cookieStr += `; Expires=${cookie.expires}`;
    }

    if (cookie.secure && environment.production) {
      cookieStr += `; Secure`;
    }

    if (cookie.sameSite) {
      cookieStr += `; SameSite=${cookie.sameSite}`;
    }

    this.document.cookie = cookieStr;
  }

  /**
   * Returns cookie with the given name or undefined if the cookie does not exist
   */
  public getCookie(name: string): string | undefined {
    const cookies = this.document.cookie.split('; ');
    const match = cookies.find(row => row.startsWith(name));

    if (match !== undefined) {
    return match.split('=')[1];
    } else {
      return undefined;
    }
  }

  /**
   * @param path Default: '/'
   */
  public deleteCookie(name: string, path = '/') {
    // Also set sameSite on delete to prevent the browsers from stating that cookie is ignored if sameSite is omitted
    this.setCookie({ name, value: '', path, sameSite: 'Lax', expires: 'Thu, 01 Jan 1970 00:00:00 UTC' });
  }
}

/**
 * All available cookie attributes except HttpOnly which cannot be set via JavaScript
 */
export interface Cookie {
  name: string;
  value: string;
  domain?: string;
  path?: string;
  maxAge?: string;
  expires?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}
